import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './PlannerDashboard.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { hasRole } from '../../utils/roleUtils';
import { useMessages } from '../../components/Messages';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toLocalDatetimeInput, parseApiDate, toLocalDateKey, formatAppDate } from '../../utils/dateUtils';

// Pure helper functions (no state dependencies) - extracted for use by tooltip components
const getPriorityColor = (priority) => {
  switch (priority) {
    case 'URGENT': return '#e74c3c';
    case 'HIGH': return '#f39c12';
    case 'NORMAL': return '#3498db';
    case 'LOW': return '#95a5a6';
    default: return '#7f8c8d';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED': return '#27ae60';
    case 'IN_PROGRESS': return '#3498db';
    case 'SCHEDULED': return '#f39c12';
    case 'PENDING': return '#95a5a6';
    default: return '#7f8c8d';
  }
};

const getResourceStatusColor = (resource) => {
  if (resource.type === 'machine') {
    switch (resource.status) {
      case 'ACTIVE': return '#4caf50';
      case 'MAINTENANCE': return '#ff9800';
      case 'BROKEN': return '#f44336';
      case 'INACTIVE': return '#9e9e9e';
      default: return '#2196f3';
    }
  } else {
    switch (resource.role) {
      case 'SUPERVISOR': return '#9c27b0';
      case 'OPERATOR': return '#2196f3';
      default: return '#607d8b';
    }
  }
};

const getResourceIcon = (resource) => {
  return resource.type === 'machine' ? '🏭' : '👷';
};

// Tooltip components with isolated state - prevents parent grid re-renders on hover
const TaskTooltipDisplay = React.forwardRef((props, ref) => {
  const { t } = useTranslation('production');
  const [data, setData] = React.useState(null);
  React.useImperativeHandle(ref, () => ({
    show: (d) => setData(d),
    hide: () => setData(null)
  }));
  if (!data) return null;
  const { x, y, task } = data;
  return (
    <div
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        backgroundColor: 'rgba(33, 33, 33, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '12px',
        zIndex: 10000,
        minWidth: '250px',
        maxWidth: '350px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        color: '#fff',
        fontSize: '12px',
        backdropFilter: 'blur(10px)',
        pointerEvents: 'none'
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
        {task.task_name || task.operation_name}
      </div>
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>{t('common:ui.code')}:</span> {task.task_code}
      </div>
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>{t('common:ui.status')}:</span>{' '}
        <span style={{
          color: task.status === 'COMPLETED' ? '#4caf50' :
                 task.status === 'IN_PROGRESS' ? '#2196f3' :
                 task.status === 'ASSIGNED' ? '#ff9800' : '#9e9e9e'
        }}>
          {task.status}
        </span>
      </div>
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.operator')}:</span> {task.operator_name || t('plannerDashboard.unassigned')}
      </div>
      {task.machine_name && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.machine')}:</span> {task.machine_name}
        </div>
      )}
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.duration')}:</span>{' '}
        {task.estimated_duration_minutes >= 60
          ? `${Math.floor(task.estimated_duration_minutes / 60)}h ${task.estimated_duration_minutes % 60}m`
          : `${task.estimated_duration_minutes}m`}
      </div>
      {task.po_number && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.po')}:</span> {task.po_number}
        </div>
      )}
      {task.priority && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('common:ui.priority')}:</span>{' '}
          <span style={{ color: getPriorityColor(task.priority) }}>
            {task.priority}
          </span>
        </div>
      )}
      {task.scheduled_start && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.scheduled')}:</span>{' '}
          {formatAppDate(task.scheduled_start, 'datetime')}
        </div>
      )}
    </div>
  );
});

const ResourceTooltipDisplay = React.forwardRef((props, ref) => {
  const { t } = useTranslation('production');
  const [data, setData] = React.useState(null);
  React.useImperativeHandle(ref, () => ({
    show: (d) => setData(d),
    hide: () => setData(null)
  }));
  if (!data) return null;
  const { x, y, resource } = data;
  return (
    <div
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        backgroundColor: 'rgba(33, 33, 33, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '12px',
        zIndex: 10000,
        minWidth: '250px',
        maxWidth: '350px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        color: '#fff',
        fontSize: '12px',
        backdropFilter: 'blur(10px)',
        pointerEvents: 'none'
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
        {getResourceIcon(resource)} {resource.display_name}
      </div>
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>
          {resource.type === 'machine' ? t('plannerDashboard.tooltip.machineCode') : t('plannerDashboard.tooltip.username')}:
        </span>{' '}
        {resource.type === 'machine' ? resource.machine_code : resource.username}
      </div>
      <div style={{ marginBottom: '4px' }}>
        <span style={{ opacity: 0.7 }}>
          {resource.type === 'machine' ? t('common:ui.type') : t('plannerDashboard.tooltip.role')}:
        </span>{' '}
        {resource.type === 'machine' ? resource.machine_type : resource.role}
      </div>
      {resource.type === 'machine' && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('common:ui.status')}:</span>{' '}
          <span style={{ color: getResourceStatusColor(resource) }}>
            {resource.status}
          </span>
        </div>
      )}
      {resource.type === 'operator' && resource.department_name && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.department')}:</span> {resource.department_name}
        </div>
      )}
      {resource.type === 'operator' && resource.shift && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.shift')}:</span> {resource.shift}
        </div>
      )}
      {resource.type === 'machine' && resource.location && (
        <div style={{ marginBottom: '4px' }}>
          <span style={{ opacity: 0.7 }}>{t('plannerDashboard.tooltip.location')}:</span> {resource.location}
        </div>
      )}
      {resource.type === 'operator' && resource.skills && resource.skills.length > 0 && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div style={{ opacity: 0.7, marginBottom: '4px', fontSize: '11px' }}>{t('plannerDashboard.tooltip.skills')}:</div>
          {resource.skills.map((skill, index) => (
            <div key={index} style={{
              marginBottom: '2px',
              fontSize: '11px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{skill.skill_name}</span>
              <span style={{
                marginLeft: '8px',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '10px',
                backgroundColor: skill.proficiency_level === 'EXPERT' ? 'rgba(76, 175, 80, 0.2)' :
                                  skill.proficiency_level === 'ADVANCED' ? 'rgba(33, 150, 243, 0.2)' :
                                  skill.proficiency_level === 'INTERMEDIATE' ? 'rgba(255, 152, 0, 0.2)' :
                                  'rgba(158, 158, 158, 0.2)',
                color: skill.proficiency_level === 'EXPERT' ? '#4caf50' :
                       skill.proficiency_level === 'ADVANCED' ? '#2196f3' :
                       skill.proficiency_level === 'INTERMEDIATE' ? '#ff9800' :
                       '#9e9e9e'
              }}>
                {skill.proficiency_level}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

function PlannerDashboard() {
  const { showError } = useMessages();
  const { user } = useAuth();
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('production');
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [draggedTask, setDraggedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedResource, setSelectedResource] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [resources, setResources] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState('');
  const [orderLines, setOrderLines] = useState([]);
  const [selectedOrderLine, setSelectedOrderLine] = useState('');
  const [allTasks, setAllTasks] = useState([]); // Store all tasks before filtering
  const [viewMode, setViewMode] = useState('day'); // 'hour', 'day'
  const [viewStartDate, setViewStartDate] = useState(new Date());
  const [notification, setNotification] = useState(null); // For showing feedback messages
  const dragOverSlotRef = useRef(null); // Track drag-over slot via DOM manipulation (no re-render)
  const [selectedTask, setSelectedTask] = useState(null); // For task detail modal
  const [showTaskModal, setShowTaskModal] = useState(false); // Control modal visibility
  const [actionHistory, setActionHistory] = useState([]); // For undo/redo
  const [historyIndex, setHistoryIndex] = useState(-1); // Current position in history
  const [contextMenu, setContextMenu] = useState(null); // For right-click context menu
  const taskTooltipRef = useRef(null); // Imperative handle for task tooltip component
  const resourceTooltipRef = useRef(null); // Imperative handle for resource tooltip component
  const [expandedMachines, setExpandedMachines] = useState(new Set()); // empty = all collapsed
  const [resourceFilter, setResourceFilter] = useState('all'); // 'all' | 'machines' | 'operators'

  // Pre-computed task lookup maps - O(1) per cell instead of O(n)
  const taskLookup = useMemo(() => {
    const byOperatorDay = {};   // key: `${operatorId}_${dateStr}` → task[]
    const byOperatorHour = {};  // key: `${operatorId}_${dateStr}_${hour}` → task[]
    const byMachineHour = {};   // key: `${machineId}_${dateStr}_${hour}` → task[]

    tasks.forEach(task => {
      if (!task.scheduled_start) return;
      const start = parseApiDate(task.scheduled_start);
      const end = task.scheduled_end ? parseApiDate(task.scheduled_end)
        : new Date(start.getTime() + (task.estimated_duration_minutes || 60) * 60000);
      const dateStr = start.toDateString();

      if (task.operator_id) {
        const dayKey = `${task.operator_id}_${dateStr}`;
        (byOperatorDay[dayKey] ??= []).push(task);

        // Index each hour the task spans
        const startHour = start.getHours();
        const endHour = Math.min(
          start.toDateString() === end.toDateString() ? end.getHours() + (end.getMinutes() > 0 ? 1 : 0) : 24,
          24
        );
        for (let h = startHour; h < endHour; h++) {
          (byOperatorHour[`${task.operator_id}_${dateStr}_${h}`] ??= []).push(task);
        }
      }
      if (task.machine_id) {
        const mStart = start.getHours();
        const mEnd = Math.min(
          start.toDateString() === end.toDateString() ? end.getHours() + (end.getMinutes() > 0 ? 1 : 0) : 24,
          24
        );
        for (let h = mStart; h < mEnd; h++) {
          (byMachineHour[`${task.machine_id}_${dateStr}_${h}`] ??= []).push(task);
        }
      }
    });
    return { byOperatorDay, byOperatorHour, byMachineHour };
  }, [tasks]);

  // Pre-computed busy slots map - replaces getBusySlots() function
  const busySlotsMap = useMemo(() => {
    const busyMap = {};
    allTasks.forEach(task => {
      if (task.status === 'ASSIGNED' || task.status === 'IN_PROGRESS' || task.status === 'SCHEDULED') {
        if (task.operator_id && (task.scheduled_start || task.scheduled_date)) {
          const operatorKey = `operator_${task.operator_id}`;
          let dateKey;
          if (task.scheduled_start) {
            dateKey = parseApiDate(task.scheduled_start).toDateString();
          } else if (task.scheduled_date) {
            const dateObj = typeof task.scheduled_date === 'string'
              ? new Date(task.scheduled_date + 'T00:00:00')
              : task.scheduled_date;
            dateKey = dateObj.toDateString();
          }
          if (!busyMap[operatorKey]) {
            busyMap[operatorKey] = {};
          }
          busyMap[operatorKey][dateKey] = {
            task: task,
            startTime: task.scheduled_start ? parseApiDate(task.scheduled_start) : null,
            endTime: task.scheduled_end ? parseApiDate(task.scheduled_end) : null
          };
        }
      }
    });
    return busyMap;
  }, [allTasks]);

  // Count nested operators per machine for collapse indicator
  const operatorCountByMachine = useMemo(() => {
    const counts = {};
    resources.forEach(r => {
      if (r.is_nested && r.parent_machine_id) {
        counts[r.parent_machine_id] = (counts[r.parent_machine_id] || 0) + 1;
      }
    });
    return counts;
  }, [resources]);

  // Filter resources based on expand/collapse state and resource filter
  const visibleResources = useMemo(() => {
    return resources.filter(resource => {
      // Resource type filter
      if (resourceFilter === 'machines' && resource.type !== 'machine') return false;
      if (resourceFilter === 'operators' && resource.type === 'machine') return false;

      // Collapse filter: hide nested operators if parent machine is not expanded
      if (resource.is_nested && resource.parent_machine_id) {
        if (resourceFilter === 'operators') return true; // show all operators in operators-only mode
        return expandedMachines.has(resource.parent_machine_id);
      }

      return true;
    });
  }, [resources, expandedMachines, resourceFilter]);

  // Toggle expand/collapse on a single machine
  const toggleMachine = useCallback((machineId) => {
    setExpandedMachines(prev => {
      const next = new Set(prev);
      if (next.has(machineId)) {
        next.delete(machineId);
      } else {
        next.add(machineId);
      }
      return next;
    });
  }, []);

  // Expand all machines
  const expandAllMachines = useCallback(() => {
    setExpandedMachines(new Set(machines.map(m => m.machine_id)));
  }, [machines]);

  // Collapse all machines
  const collapseAllMachines = useCallback(() => {
    setExpandedMachines(new Set());
  }, []);

  // Generate time slots based on view mode
  useEffect(() => {
    const generateTimeSlots = () => {
      const slots = [];
      const start = new Date(viewStartDate);
      start.setHours(0, 0, 0, 0);

      switch (viewMode) {
        case 'hour':
          // Show all 24 hours with scrolling
          for (let i = 0; i < 24; i++) {
            // Create a new date object for each hour slot to avoid reference issues
            const slotTime = new Date(start.getFullYear(), start.getMonth(), start.getDate(), i, 0, 0, 0);

            // Format hour display (12 AM, 1 AM, ... 11 PM)
            let displayHour = i;
            let period = 'AM';
            if (i === 0) {
              displayHour = 12;
            } else if (i === 12) {
              period = 'PM';
            } else if (i > 12) {
              displayHour = i - 12;
              period = 'PM';
            }

            slots.push({
              date: slotTime,
              displayText: `${displayHour} ${period}`,
              headerText: i === 0 ? formatAppDate(start, { weekday: 'short', month: 'short', day: 'numeric' }) : '',
              isWorkingHour: i >= 8 && i < 17,
              isToday: start.toDateString() === new Date().toDateString(),
              isWeekend: start.getDay() === 0 || start.getDay() === 6,
              type: 'hour',
              hour: i
            });
          }
          break;

        case 'day':
          // Start from the Sunday of the week containing viewStartDate
          const weekStart = new Date(viewStartDate);
          const dayOfWeek = weekStart.getDay();
          weekStart.setDate(weekStart.getDate() - dayOfWeek); // Go back to Sunday
          weekStart.setHours(0, 0, 0, 0);

          // Show 28 days (4 weeks) starting from that Sunday
          for (let i = 0; i < 28; i++) {
            const date = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
            const isInCurrentWeek = Math.floor(i / 7) === Math.floor(dayOfWeek / 7) ||
                                   (dayOfWeek === 0 && i < 7); // Handle Sunday edge case

            slots.push({
              date: date,
              displayText: date.getDate().toString(),
              headerText: formatAppDate(date, { weekday: 'short' }),
              dayName: formatAppDate(date, { weekday: 'short' }),
              dayNumber: date.getDate(),
              month: formatAppDate(date, { month: 'short' }),
              isToday: date.toDateString() === new Date().toDateString(),
              isWeekend: date.getDay() === 0 || date.getDay() === 6,
              isCurrentDate: date.toDateString() === viewStartDate.toDateString(),
              type: 'day'
            });
          }
          break;

        case 'week':
          // Show 12 weeks with scrolling
          for (let i = 0; i < 12; i++) {
            const weekStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (i * 7));
            const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);

            slots.push({
              date: weekStart,
              weekStart: weekStart,
              weekEnd: weekEnd,
              displayText: `W${Math.ceil((weekStart.getDate() + weekStart.getDay()) / 7)}`,
              headerText: `${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`,
              isToday: false,
              isCurrentWeek: weekStart <= new Date() && weekEnd >= new Date(),
              type: 'week'
            });
          }
          break;

        case 'month':
          // Show 6 months
          for (let i = 0; i < 6; i++) {
            const monthDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
            slots.push({
              date: monthDate,
              displayText: formatAppDate(monthDate, { month: 'short' }),
              headerText: monthDate.getFullYear().toString(),
              month: monthDate.getMonth(),
              year: monthDate.getFullYear(),
              isCurrentMonth: monthDate.getMonth() === new Date().getMonth() && monthDate.getFullYear() === new Date().getFullYear(),
              type: 'month'
            });
          }
          break;
      }

      return slots;
    };

    setTimeSlots(generateTimeSlots());
  }, [viewMode, viewStartDate]);

  // Fetch machines and operators
  useEffect(() => {
    fetchMachines();
    fetchOperators();
    fetchActiveTasks();
    fetchProductionOrders();
  }, []);

  // Re-fetch tasks whenever a task is saved from any form (e.g. ViewTask assignment)
  useEffect(() => {
    const handleTaskUpdated = () => fetchActiveTasks();
    window.addEventListener('task-updated', handleTaskUpdated);
    return () => window.removeEventListener('task-updated', handleTaskUpdated);
  }, []);

  // Handle task click to open detail modal
  const handleTaskClick = (e, task) => {
    e.stopPropagation(); // Prevent triggering slot click
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  // Handle task hover - show tooltip (ref-based, no re-render)
  const handleTaskMouseEnter = useCallback((e, task) => {
    const rect = e.currentTarget.getBoundingClientRect();
    taskTooltipRef.current?.show({
      x: rect.right + 10,
      y: rect.top,
      task: task
    });
  }, []);

  const handleTaskMouseLeave = useCallback(() => {
    taskTooltipRef.current?.hide();
  }, []);

  // Handle right-click on task
  const handleTaskContextMenu = (e, task) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      task: task
    });
  };

  // Handle context menu action
  const handleContextMenuAction = (action) => {
    const task = contextMenu.task;

    switch (action) {
      case 'edit':
        setSelectedTask(task);
        setShowTaskModal(true);
        break;
      case 'complete':
        handleTaskUpdate({ status: 'COMPLETED' });
        showNotification(t('plannerDashboard.notifications.markedCompleted'), 'success');
        break;
      case 'high-priority':
        handleQuickUpdate(task, { priority: 'HIGH' });
        break;
      case 'urgent-priority':
        handleQuickUpdate(task, { priority: 'URGENT' });
        break;
      case 'normal-priority':
        handleQuickUpdate(task, { priority: 'NORMAL' });
        break;
      case 'delete':
        handleTaskDelete(task.task_id);
        break;
      case 'duplicate':
        handleDuplicateTask(task);
        break;
    }

    setContextMenu(null);
  };

  // Quick update task without opening modal
  const handleQuickUpdate = async (task, updates) => {
    try {

      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update task');
      }

      // Update local state
      const updatedTasks = tasks.map(t =>
        t.task_id === task.task_id ? { ...t, ...updates } : t
      );
      const updatedAllTasks = allTasks.map(t =>
        t.task_id === task.task_id ? { ...t, ...updates } : t
      );

      setTasks(updatedTasks);
      setAllTasks(updatedAllTasks);

      showNotification(t('plannerDashboard.notifications.updated'), 'success');
    } catch (error) {
      console.error('Error updating task:', error);
      showNotification(t('plannerDashboard.notifications.updateFailed'), 'error');
    }
  };

  // Duplicate task
  const handleDuplicateTask = async (task) => {
    try {
      const token = localStorage.getItem('token');

      const newTask = {
        ...task,
        task_code: `${task.task_code}-COPY`,
        task_name: `${task.task_name} (Copy)`,
        status: 'PENDING',
        scheduled_start: null,
        scheduled_end: null,
        operator_id: null,
        assigned_to: null
      };

      delete newTask.task_id;
      delete newTask.created_at;
      delete newTask.updated_at;

      const response = await apiFetch(getApiUrl('/api/production-tasks/tasks'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTask)
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate task');
      }

      const createdTask = await response.json();

      // Add to local state
      setTasks([...tasks, createdTask]);
      setAllTasks([...allTasks, createdTask]);

      showNotification(t('plannerDashboard.notifications.duplicated'), 'success');
    } catch (error) {
      console.error('Error duplicating task:', error);
      showNotification(t('plannerDashboard.notifications.duplicateFailed'), 'error');
    }
  };

  // Handle task modal close
  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  // Handle task update from modal
  const handleTaskUpdate = async (updatedTaskData) => {
    try {
      const token = localStorage.getItem('token');

      // Update task via API
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTaskData)
      });

      if (!response.ok) {
        throw new Error('Failed to update task');
      }

      // Save current state to history for undo
      const historyEntry = {
        action: 'update',
        taskId: selectedTask.task_id,
        previousState: { ...selectedTask },
        newState: { ...selectedTask, ...updatedTaskData },
        timestamp: new Date().toISOString()
      };

      // Add to history and update index
      const newHistory = [...actionHistory.slice(0, historyIndex + 1), historyEntry];
      setActionHistory(newHistory.slice(-20)); // Keep last 20 actions
      setHistoryIndex(newHistory.length - 1);

      // Update local state
      const updatedTasks = tasks.map(task =>
        task.task_id === selectedTask.task_id ? { ...task, ...updatedTaskData } : task
      );
      const updatedAllTasks = allTasks.map(task =>
        task.task_id === selectedTask.task_id ? { ...task, ...updatedTaskData } : task
      );

      setTasks(updatedTasks);
      setAllTasks(updatedAllTasks);
      setShowTaskModal(false);
      setSelectedTask(null);

      showNotification(t('plannerDashboard.notifications.updated'), 'success');
    } catch (error) {
      console.error('Error updating task:', error);
      showNotification(t('plannerDashboard.notifications.updateFailed'), 'error');
    }
  };

  // Handle undo action
  const handleUndo = async () => {
    if (historyIndex < 0 || actionHistory.length === 0) {
      showNotification(t('plannerDashboard.notifications.nothingToUndo'), 'info');
      return;
    }

    const action = actionHistory[historyIndex];

    try {
      const token = localStorage.getItem('token');

      if (action.action === 'move') {
        // Restore task to previous position
        const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${action.taskId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scheduled_start: action.previousState.scheduled_start,
            scheduled_end: action.previousState.scheduled_end,
            operator_id: action.previousState.operator_id,
            machine_id: action.previousState.machine_id
          })
        });

        if (response.ok) {
          // Update local state
          const updatedTasks = tasks.map(task =>
            task.task_id === action.taskId ? { ...task, ...action.previousState } : task
          );
          setTasks(updatedTasks);
          setHistoryIndex(historyIndex - 1);
          showNotification(t('plannerDashboard.notifications.undoneMoved'), 'info');
        }
      } else if (action.action === 'update') {
        // Restore task to previous values
        const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${action.taskId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(action.previousState)
        });

        if (response.ok) {
          const updatedTasks = tasks.map(task =>
            task.task_id === action.taskId ? action.previousState : task
          );
          setTasks(updatedTasks);
          setHistoryIndex(historyIndex - 1);
          showNotification(t('plannerDashboard.notifications.undoneRestored'), 'info');
        }
      }
    } catch (error) {
      console.error('Error during undo:', error);
      showNotification(t('plannerDashboard.notifications.undoFailed'), 'error');
    }
  };

  // Handle redo action
  const handleRedo = async () => {
    if (historyIndex >= actionHistory.length - 1) {
      showNotification(t('plannerDashboard.notifications.nothingToRedo'), 'info');
      return;
    }

    const action = actionHistory[historyIndex + 1];

    try {
      const token = localStorage.getItem('token');

      if (action.action === 'move' || action.action === 'update') {
        const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${action.taskId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(action.newState)
        });

        if (response.ok) {
          const updatedTasks = tasks.map(task =>
            task.task_id === action.taskId ? action.newState : task
          );
          setTasks(updatedTasks);
          setHistoryIndex(historyIndex + 1);
          showNotification(t('plannerDashboard.notifications.redone'), 'info');
        }
      }
    } catch (error) {
      console.error('Error during redo:', error);
      showNotification(t('plannerDashboard.notifications.redoFailed'), 'error');
    }
  };

  // Handle task deletion
  const handleTaskDelete = async (taskId) => {
    if (!window.confirm(t('plannerDashboard.confirmDelete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // Delete task via API
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete task');
      }

      // Save to history for undo
      const deletedTask = tasks.find(t => t.task_id === taskId);
      const historyEntry = {
        action: 'delete',
        taskId: taskId,
        previousState: { ...deletedTask },
        timestamp: new Date().toISOString()
      };

      const newHistory = [...actionHistory.slice(0, historyIndex + 1), historyEntry];
      setActionHistory(newHistory.slice(-20));
      setHistoryIndex(newHistory.length - 1);

      // Update local state
      const updatedTasks = tasks.filter(task => task.task_id !== taskId);
      const updatedAllTasks = allTasks.filter(task => task.task_id !== taskId);

      setTasks(updatedTasks);
      setAllTasks(updatedAllTasks);
      setShowTaskModal(false);
      setSelectedTask(null);

      showNotification(t('plannerDashboard.notifications.deleted'), 'success');
    } catch (error) {
      console.error('Error deleting task:', error);
      showNotification(t('plannerDashboard.notifications.deleteFailed'), 'error');
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Cleanup any stray components from other pages (e.g., order tracer)
  useEffect(() => {
    // Hide any order tracer elements that might be persisting
    const hideOrderTracerElements = () => {
      // Find and hide any elements that might be from order tracer
      const tracerElements = document.querySelectorAll('[class*="tracer"], [class*="trace"], [class*="order-trace"]');
      tracerElements.forEach(element => {
        if (element.style) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.zIndex = '-1';
        }
      });

      // Hide any modal overlays that might be persisting
      const modalOverlays = document.querySelectorAll('.modal-overlay, .overlay, [style*="position: fixed"]');
      modalOverlays.forEach(overlay => {
        if (overlay.classList.contains('modal-overlay') ||
            (overlay.style.position === 'fixed' && overlay.style.zIndex > 1000)) {
          overlay.style.display = 'none';
        }
      });
    };

    // Run cleanup immediately when planner loads
    hideOrderTracerElements();

    // Run cleanup again after a short delay to catch any delayed renders
    const cleanupTimer = setTimeout(hideOrderTracerElements, 100);

    return () => clearTimeout(cleanupTimer);
  }, []);

  // Add keyboard event listener for ESC key and undo/redo
  useEffect(() => {
    const handleKeyDown = (event) => {
      // ESC key handling
      if (event.key === 'Escape') {
        if (contextMenu) {
          setContextMenu(null);
        } else if (showTaskModal) {
          handleCloseTaskModal();
        } else if (viewMode === 'hour') {
          setViewMode('day');
          showNotification(t('plannerDashboard.notifications.switchedBackToDay'), 'info');
        }
      }

      // Undo (Ctrl+Z or Cmd+Z)
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      }

      // Redo (Ctrl+Y or Cmd+Y or Ctrl+Shift+Z)
      if (((event.ctrlKey || event.metaKey) && event.key === 'y') ||
          ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')) {
        event.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewMode, showTaskModal, historyIndex, actionHistory, contextMenu]);

  // Combine machines and operators into resources
  useEffect(() => {
    const combinedResources = [];

    // Group operators by department
    const operatorsByDept = {};
    operators.forEach(operator => {
      const deptId = operator.department_id || 0;
      if (!operatorsByDept[deptId]) {
        operatorsByDept[deptId] = [];
      }
      operatorsByDept[deptId].push({
        ...operator,
        type: 'operator',
        display_name: `${operator.first_name} ${operator.last_name}`,
        resource_id: `operator_${operator.user_id}`
      });
    });


    // Track which operators have been added to avoid duplicates
    const addedOperators = new Set();

    // Process machines and add their department's operators after each machine
    machines.forEach((machine, machineIndex) => {
      // Add the machine
      combinedResources.push({
        ...machine,
        type: 'machine',
        display_name: machine.machine_name,
        resource_id: `machine_${machine.machine_id}`
      });

      // Add operators from the same department right after the machine
      const deptOperators = operatorsByDept[machine.department_id] || [];

      deptOperators.forEach(operator => {
        // Create unique key for each operator under each machine
        const uniqueResourceId = `machine_${machine.machine_id}_operator_${operator.user_id}`;

        combinedResources.push({
          ...operator,
          parent_machine_id: machine.machine_id,
          is_nested: true,
          resource_id: uniqueResourceId  // Override with unique ID
        });
      });
    });

    setResources(combinedResources);
  }, [machines, operators]);

  const fetchMachines = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/machines'));
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      } else {
        setMachines([]);
        showError(tCommon('errors.genericTitle'), tCommon('errors.machinesLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching machines:', error);
      setMachines([]);
      showError(tCommon('errors.genericTitle'), tCommon('errors.machinesLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const data = await response.json();
        // Filter for operators and supervisors only
        const operators = data.filter(u =>
          hasRole(u, 'OPERATOR', 'SUPERVISOR')
        );

        // Fetch skills for each operator
        const operatorsWithSkills = await Promise.all(
          operators.map(async (operator) => {
            try {
              const skillsResponse = await apiFetch(getApiUrl(`/api/users/${operator.user_id}/skills`));
              if (skillsResponse.ok) {
                const skillsData = await skillsResponse.json();
                return { ...operator, skills: skillsData.skills || skillsData || [] };
              }
            } catch (err) {
              // Silently handle error
            }
            return { ...operator, skills: [] };
          })
        );

        setOperators(operatorsWithSkills);
      } else {
        setOperators([]);
        showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching operators:', error);
      setOperators([]);
      showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
    }
  };

  const fetchActiveTasks = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production-tasks/tasks?include_completed=true'));
      if (response.ok) {
        const data = await response.json();
        // Map assigned_to field to operator_id for consistency
        const mappedTasks = data.map(task => ({
          ...task,
          operator_id: task.operator_id || task.assigned_to,
          scheduled_date: task.scheduled_start ? toLocalDateKey(task.scheduled_start) : task.scheduled_date
        }));
        setAllTasks(mappedTasks); // Store all tasks
        setTasks(mappedTasks); // Initially show all tasks
      } else {
        setAllTasks([]);
        setTasks([]);
        showError(tCommon('errors.genericTitle'), tCommon('errors.tasksLoadFailed'));
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setAllTasks([]);
      setTasks([]);
      showError(tCommon('errors.genericTitle'), tCommon('errors.tasksLoadFailed'));
    }
  };

  const fetchProductionOrders = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production/orders?status=RELEASED'));
      if (response.ok) {
        const data = await response.json();
        // Filter for RELEASED status in case the API doesn't filter
        const releasedOrders = (data.orders || data).filter(po => po.status === 'RELEASED');
        setProductionOrders(releasedOrders);
      }
    } catch (error) {
      console.error('Error fetching production orders:', error);
    }
  };

  const fetchOrderLines = async (poId) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${poId}/lines`));
      if (response.ok) {
        const data = await response.json();
        setOrderLines(data);
      }
    } catch (error) {
      console.error('Error fetching order lines:', error);
      setOrderLines([]);
    }
  };

  // Mark tasks based on selection - show all but differentiate selected PO
  useEffect(() => {
    if (!selectedPO) {
      // No PO selected - show all tasks normally
      setTasks(allTasks.map(task => ({ ...task, isCurrentPO: true })));
      return;
    }

    // Extract po_number from the composite value
    const poNumber = selectedPO.split('|')[1];

    // Mark tasks as belonging to current PO or not
    let markedTasks = allTasks.map(task => {
      // Check if task matches current PO
      const poMatch = task.po_number === poNumber;

      // If a specific order line is selected, check that too
      const lineMatch = !selectedOrderLine ||
                       task.production_order_line_id === parseInt(selectedOrderLine);

      return {
        ...task,
        isCurrentPO: poMatch && lineMatch,
        isOtherPO: !poMatch
      };
    });

    setTasks(markedTasks);
  }, [selectedPO, selectedOrderLine, allTasks]);

  // Handle PO selection change
  const handlePOChange = (e) => {
    const poValue = e.target.value;
    setSelectedPO(poValue);
    setSelectedOrderLine(''); // Reset order line selection
    setOrderLines([]); // Clear order lines

    if (poValue) {
      // Extract the PO ID from the value to fetch lines
      const poId = poValue.split('|')[1]; // We'll store "id|po_number" as value, use po_number for API
      fetchOrderLines(poId);
    }
  };

  // Show notification helper
  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = useCallback((e, resourceId, slot) => {
    e.preventDefault();

    // Remove class from previous slot element
    if (dragOverSlotRef.current?.element) {
      dragOverSlotRef.current.element.classList.remove('drag-over-valid', 'drag-over-invalid');
    }

    // Parse resource type to determine if this is a valid drop target
    let resourceType, actualId;
    if (resourceId.startsWith('machine_') && resourceId.includes('_operator_')) {
      const parts = resourceId.split('_');
      resourceType = 'operator';
      actualId = parts[parts.length - 1];
    } else {
      [resourceType, actualId] = resourceId.split('_');
    }

    // Determine drop effect based on validity and update DOM directly
    let valid;
    if (resourceType === 'machine') {
      e.dataTransfer.dropEffect = 'none';
      valid = false;
    } else if (!isSlotAvailable(resourceId, slot)) {
      e.dataTransfer.dropEffect = 'none';
      valid = false;
    } else {
      e.dataTransfer.dropEffect = 'move';
      valid = true;
    }

    // Add class to current slot element directly (no state update)
    const el = e.currentTarget;
    el.classList.add(valid ? 'drag-over-valid' : 'drag-over-invalid');
    dragOverSlotRef.current = { element: el, valid };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragLeave = useCallback(() => {
    if (dragOverSlotRef.current?.element) {
      dragOverSlotRef.current.element.classList.remove('drag-over-valid', 'drag-over-invalid');
      dragOverSlotRef.current = null;
    }
  }, []);

  const handleDrop = async (e, resourceId, dateSlot) => {
    e.preventDefault();
    if (!draggedTask) return;

    // Parse resource type and ID - handle both simple and composite IDs
    let resourceType, actualId, machineId = null;

    if (resourceId.startsWith('machine_') && resourceId.includes('_operator_')) {
      // Composite ID like "machine_1_operator_5"
      const parts = resourceId.split('_');
      resourceType = 'operator';
      machineId = parts[1]; // Extract the machine ID
      actualId = parts[parts.length - 1]; // Get the operator ID from the end
    } else {
      // Simple ID like "machine_1" or "operator_5"
      [resourceType, actualId] = resourceId.split('_');
    }

    // Clear drag over visual feedback via DOM
    if (dragOverSlotRef.current?.element) {
      dragOverSlotRef.current.element.classList.remove('drag-over-valid', 'drag-over-invalid');
      dragOverSlotRef.current = null;
    }

    // Prevent dropping tasks on machines
    if (resourceType === 'machine') {
      showNotification(t('plannerDashboard.notifications.onlyOperators'), 'error');
      return;
    }

    // Check if the slot is available (skip for day view since we auto-find available hour)
    if (viewMode !== 'day' && !isSlotAvailable(resourceId, dateSlot)) {
      showNotification(t('plannerDashboard.notifications.slotOccupied'), 'warning');
      return;
    }

    // Check if this is a valid drop based on time constraints
    // For hour view, check if the target hour is within working hours
    if (resourceType === 'operator' && dateSlot.type === 'hour') {
      const targetHour = dateSlot.hour;
      const isTargetWorkingHours = targetHour >= 8 && targetHour < 17;

      // Optional: Only enforce working hours restriction if needed
      // Commenting out for now to allow flexible scheduling
      // if (!isTargetWorkingHours) {
      //   showNotification('🕐 Consider: This is outside normal working hours (8 AM - 5 PM)', 'warning');
      //   // Don't return - just warn but allow the assignment
      // }
    }

    // Calculate scheduled start and end times based on view mode and task duration
    let scheduledStart, dateString;

    // Helper to create a local date without timezone issues
    const createLocalDate = (date, hours = 9, minutes = 0) => {
      const localDate = new Date(date);
      localDate.setHours(hours, minutes, 0, 0);
      return localDate;
    };

    switch (dateSlot.type) {
      case 'hour':
        // For hourly view, use the specific hour
        scheduledStart = new Date(dateSlot.date);
        break;

      case 'day':
        // For daily view, find the first available hour slot
        const availableHour = findFirstAvailableHour(actualId, dateSlot.date);
        scheduledStart = createLocalDate(dateSlot.date, availableHour, 0);
        showNotification(t('plannerDashboard.notifications.scheduledAt', { hour: availableHour, date: formatAppDate(dateSlot.date) }), 'success');
        break;

      case 'week':
        // For weekly view, use the start of the week at 9 AM
        scheduledStart = createLocalDate(dateSlot.weekStart, 9, 0);
        break;

      case 'month':
        // For monthly view, use the first working day of the month at 9 AM
        const monthStart = new Date(dateSlot.date);
        // Skip to first weekday if it starts on weekend
        while (monthStart.getDay() === 0 || monthStart.getDay() === 6) {
          monthStart.setDate(monthStart.getDate() + 1);
        }
        scheduledStart = createLocalDate(monthStart, 9, 0);
        break;

      default:
        scheduledStart = createLocalDate(dateSlot.date, 9, 0);
    }

    // Check if the date is valid
    if (isNaN(scheduledStart.getTime())) {
      console.error('Invalid date:', scheduledStart);
      showNotification(t('plannerDashboard.notifications.invalidDate'), 'error');
      return;
    }

    // Use task's estimated duration (default to 60 minutes if not specified)
    const taskDuration = draggedTask.estimated_duration_minutes || 60;
    const scheduledEnd = new Date(scheduledStart);
    scheduledEnd.setMinutes(scheduledEnd.getMinutes() + taskDuration);

    // Update task in database
    try {
      const token = localStorage.getItem('token');

      // Update task with scheduling information and machine assignment
      const taskUpdateData = {
        status: 'ASSIGNED',
        // The API stores UTC; send the instant, not local wall-clock time
        scheduled_start: scheduledStart.toISOString(),
        scheduled_end: scheduledEnd.toISOString(),
      };

      // Add machine_id if task is being assigned to a machine-operator combination
      if (machineId) {
        taskUpdateData.machine_id = parseInt(machineId);
      }

      const taskUpdateResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${draggedTask.task_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskUpdateData),
      });

      if (!taskUpdateResponse.ok) {
        throw new Error('Failed to update task');
      }

      // Create task assignment to operator
      const assignmentResponse = await apiFetch(getApiUrl('/api/production-tasks/assignments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: draggedTask.task_id,
          assigned_to: parseInt(actualId),
          assigned_by: user?.user_id,
          is_primary: true,
          assignment_notes: `Scheduled via planner on ${formatAppDate(new Date())}`
        }),
      });

      if (!assignmentResponse.ok) {
        throw new Error('Failed to create task assignment');
      }

      // Save to history for undo
      const historyEntry = {
        action: 'move',
        taskId: draggedTask.task_id,
        previousState: {
          scheduled_start: draggedTask.scheduled_start,
          scheduled_end: draggedTask.scheduled_end,
          operator_id: draggedTask.operator_id,
          machine_id: draggedTask.machine_id,
          assigned_to: draggedTask.assigned_to,
          status: draggedTask.status
        },
        newState: {
          scheduled_start: scheduledStart.toISOString(),
          scheduled_end: scheduledEnd.toISOString(),
          operator_id: parseInt(actualId),
          machine_id: machineId ? parseInt(machineId) : null,
          assigned_to: parseInt(actualId),
          status: 'ASSIGNED'
        },
        timestamp: new Date().toISOString()
      };

      const newHistory = [...actionHistory.slice(0, historyIndex + 1), historyEntry];
      setActionHistory(newHistory.slice(-20)); // Keep last 20 actions
      setHistoryIndex(newHistory.length - 1);

      // Update local state after successful API calls
      const updatedTask = {
        ...draggedTask,
        resource_id: resourceId,
        resource_type: resourceType,
        machine_id: machineId ? parseInt(machineId) : null,
        operator_id: parseInt(actualId),
        assigned_to: parseInt(actualId), // Also update assigned_to field
        scheduled_date: toLocalDateKey(scheduledStart), // Local YYYY-MM-DD
        scheduled_start: scheduledStart.toISOString(),
        scheduled_end: scheduledEnd.toISOString(),
        status: 'ASSIGNED'
      };

      // Update both tasks and allTasks arrays
      const updatedTasks = tasks.map(task =>
        task.task_id === draggedTask.task_id ? updatedTask : task
      );

      const updatedAllTasks = allTasks.map(task =>
        task.task_id === draggedTask.task_id ? updatedTask : task
      );

      setTasks(updatedTasks);
      setAllTasks(updatedAllTasks); // Important: update allTasks so busySlotsMap recalculates
      setDraggedTask(null);

      showNotification(t('plannerDashboard.notifications.assigned', { code: draggedTask.task_code }), 'success');
    } catch (error) {
      console.error('Error saving task assignment:', error);
      showNotification(t('plannerDashboard.notifications.assignmentFailed'), 'error');
      setDraggedTask(null);
    }
  };

  // Handle unscheduling a task (dragging back to unscheduled area)
  const handleUnscheduleTask = async (e) => {
    e.preventDefault();
    if (!draggedTask) return;

    // Only allow unscheduling if the task is actually scheduled
    if (!draggedTask.scheduled_start && !draggedTask.scheduled_date) {
      showNotification(t('plannerDashboard.notifications.alreadyUnscheduled'), 'warning');
      setDraggedTask(null);
      return;
    }

    try {
      // Update task to remove scheduling
      const taskUpdateData = {
        status: 'PENDING',
        scheduled_start: null,
        scheduled_end: null,
        machine_id: null
      };

      const taskUpdateResponse = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${draggedTask.task_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskUpdateData),
      });

      if (!taskUpdateResponse.ok) {
        throw new Error('Failed to update task');
      }

      // Delete task assignment if it exists
      if (draggedTask.assigned_to || draggedTask.operator_id) {
        // Find and delete the assignment
        const assignmentResponse = await apiFetch(getApiUrl(`/api/production-tasks/assignments/task/${draggedTask.task_id}`), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Don't throw error if assignment doesn't exist
        if (!assignmentResponse.ok && assignmentResponse.status !== 404) {
          console.warn('Failed to delete task assignment, but continuing...');
        }
      }

      // Save to history for undo
      const historyEntry = {
        action: 'unschedule',
        taskId: draggedTask.task_id,
        previousState: {
          scheduled_start: draggedTask.scheduled_start,
          scheduled_end: draggedTask.scheduled_end,
          operator_id: draggedTask.operator_id,
          machine_id: draggedTask.machine_id,
          assigned_to: draggedTask.assigned_to,
          status: draggedTask.status
        },
        newState: {
          scheduled_start: null,
          scheduled_end: null,
          operator_id: null,
          machine_id: null,
          assigned_to: null,
          status: 'PENDING'
        },
        timestamp: new Date().toISOString()
      };

      const newHistory = [...actionHistory.slice(0, historyIndex + 1), historyEntry];
      setActionHistory(newHistory.slice(-20));
      setHistoryIndex(newHistory.length - 1);

      // Update local state
      const updatedTask = {
        ...draggedTask,
        resource_id: null,
        resource_type: null,
        machine_id: null,
        operator_id: null,
        assigned_to: null,
        scheduled_date: null,
        scheduled_start: null,
        scheduled_end: null,
        status: 'PENDING'
      };

      const updatedTasks = tasks.map(task =>
        task.task_id === draggedTask.task_id ? updatedTask : task
      );

      const updatedAllTasks = allTasks.map(task =>
        task.task_id === draggedTask.task_id ? updatedTask : task
      );

      setTasks(updatedTasks);
      setAllTasks(updatedAllTasks);
      setDraggedTask(null);

      showNotification(t('plannerDashboard.notifications.movedToUnscheduled', { code: draggedTask.task_code }), 'success');
    } catch (error) {
      console.error('Error unscheduling task:', error);
      showNotification(t('plannerDashboard.notifications.unscheduleFailed'), 'error');
      setDraggedTask(null);
    }
  };

  const handleUnscheduledDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // getBusySlots replaced by busySlotsMap useMemo above

  // Find first available hour slot in a day for an operator (uses lookup map)
  const findFirstAvailableHour = (operatorId, date) => {
    const dateStr = new Date(date).toDateString();
    const opId = parseInt(operatorId);

    // Check each working hour (8 AM to 5 PM)
    for (let hour = 8; hour < 17; hour++) {
      const key = `${opId}_${dateStr}_${hour}`;
      const tasksInSlot = taskLookup.byOperatorHour[key] || [];

      const hasConflict = tasksInSlot.some(task => {
        if (draggedTask && task.task_id === draggedTask.task_id) return false;
        return true;
      });

      if (!hasConflict) {
        return hour; // Return the first available hour
      }
    }

    // If no hour is available during working hours, return 9 AM as fallback
    return 9;
  };

  // Check if a slot is available for a resource (uses lookup map)
  const isSlotAvailable = (resourceId, dateSlot) => {
    // Parse resource type
    let resourceType, actualId;
    if (resourceId.startsWith('machine_') && resourceId.includes('_operator_')) {
      const parts = resourceId.split('_');
      resourceType = 'operator';
      actualId = parts[parts.length - 1];
    } else {
      [resourceType, actualId] = resourceId.split('_');
    }

    // Machines are never available for direct assignment
    if (resourceType === 'machine') {
      return false;
    }

    // For hour view, check if the specific hour is available via lookup
    if (viewMode === 'hour' && dateSlot.type === 'hour') {
      const key = `${parseInt(actualId)}_${dateSlot.date.toDateString()}_${dateSlot.date.getHours()}`;
      const tasksInSlot = taskLookup.byOperatorHour[key] || [];

      const hasConflict = tasksInSlot.some(task => {
        // Exclude the currently dragged task from conflict checking
        if (draggedTask && task.task_id === draggedTask.task_id) return false;
        return true;
      });

      return !hasConflict;
    }

    // For day view, always allow drops since actual scheduling happens at hour level
    if (viewMode === 'day' && dateSlot.type === 'day') {
      return true;
    }

    // For other views, check if slot is occupied via pre-computed busy map
    const dateKey = dateSlot.date.toDateString();
    const operatorKey = `operator_${actualId}`;
    return !busySlotsMap[operatorKey]?.[dateKey];
  };

  // Calculate task span info for multi-slot display
  const getTaskSpanInfo = (task, slot, slotIndex, allSlots) => {
    if (!task || !task.scheduled_start) return null;

    const taskStart = parseApiDate(task.scheduled_start);
    const taskEnd = task.scheduled_end ? parseApiDate(task.scheduled_end) : new Date(taskStart.getTime() + (task.estimated_duration_minutes || 60) * 60000);

    // For hour view, calculate span across hours
    if (viewMode === 'hour' && slot.type === 'hour') {
      const slotStart = new Date(slot.date);
      const slotEnd = new Date(slotStart);
      slotEnd.setHours(slotEnd.getHours() + 1);

      // Check if task starts in this slot
      const isStart = taskStart >= slotStart && taskStart < slotEnd;

      // Check if task continues through this slot
      const continuesThrough = taskStart < slotStart && taskEnd > slotStart;

      // Check if task ends in this slot
      const isEnd = taskEnd > slotStart && taskEnd <= slotEnd;

      if (isStart) {
        // Calculate how many hour slots this task spans
        const durationMinutes = (taskEnd - taskStart) / (1000 * 60);
        const spanCount = Math.ceil(durationMinutes / 60);

        // Make sure we don't span beyond the available slots
        const remainingSlots = allSlots.length - slotIndex;
        const actualSpan = Math.min(spanCount, remainingSlots);

        return {
          isStart: true,
          isEnd: spanCount === 1,
          continuesThrough: false,
          spanCount: actualSpan,
          shouldDisplay: true
        };
      } else if (continuesThrough) {
        // This slot is covered by a task that started earlier
        return {
          isStart: false,
          isEnd: false,
          continuesThrough: true,
          spanCount: 0,
          shouldDisplay: false
        };
      }
    }

    // For day view, check if task spans multiple days
    if (viewMode === 'day' && slot.type === 'day') {
      const slotDate = new Date(slot.date);
      slotDate.setHours(0, 0, 0, 0);
      const taskStartDate = new Date(taskStart);
      taskStartDate.setHours(0, 0, 0, 0);
      const taskEndDate = new Date(taskEnd);
      taskEndDate.setHours(0, 0, 0, 0);

      // Check if task starts on this day
      if (taskStartDate.getTime() === slotDate.getTime()) {
        // Calculate days span
        const daySpan = Math.ceil((taskEndDate - taskStartDate) / (1000 * 60 * 60 * 24)) + 1;
        const remainingSlots = allSlots.length - slotIndex;
        const actualSpan = Math.min(daySpan, remainingSlots);

        return {
          isStart: true,
          isEnd: daySpan === 1,
          continuesThrough: false,
          spanCount: actualSpan,
          shouldDisplay: true
        };
      } else if (taskStartDate < slotDate && taskEndDate >= slotDate) {
        // Task continues through this day
        return {
          isStart: false,
          continuesThrough: true,
          spanCount: 0,
          shouldDisplay: false
        };
      }
    }

    // For week and month views, just show in single slot for now
    if (task.scheduled_start) {
      const taskStartStr = parseApiDate(task.scheduled_start).toDateString();
      const slotDateStr = slot.date.toDateString();

      if (taskStartStr === slotDateStr) {
        return {
          isStart: true,
          isEnd: true,
          spanCount: 1,
          shouldDisplay: true
        };
      }
    }

    return null;
  };

  // Get all tasks for a specific day slot (uses lookup map)
  const getAllTasksForDaySlot = (operatorId, slot) => {
    if (slot.type !== 'day') return [];
    const key = `${parseInt(operatorId)}_${slot.date.toDateString()}`;
    return taskLookup.byOperatorDay[key] || [];
  };

  // Check if tasks overlap on the same machine (uses lookup map)
  const checkMachineConflicts = (machineId, slot) => {
    if (!machineId || viewMode !== 'hour') return [];

    const key = `${parseInt(machineId)}_${slot.date.toDateString()}_${slot.date.getHours()}`;
    const tasksInSlot = taskLookup.byMachineHour[key] || [];

    // Filter out completed and cancelled tasks
    const activeTasks = tasksInSlot.filter(t =>
      t.status !== 'CANCELLED' && t.status !== 'COMPLETED'
    );

    // Return conflicts if more than one active task overlaps
    return activeTasks.length > 1 ? activeTasks : [];
  };

  // Get ANY task in a slot for display (uses lookup map for day/hour, fallback for week/month)
  const getTaskForDisplay = (resourceId, slot) => {
    // Parse resource type
    let resourceType, actualId, machineId = null;
    if (resourceId.startsWith('machine_') && resourceId.includes('_operator_')) {
      const parts = resourceId.split('_');
      resourceType = 'operator';
      machineId = parts[1];
      actualId = parts[parts.length - 1];
    } else {
      [resourceType, actualId] = resourceId.split('_');
    }

    if (resourceType !== 'operator') return null;

    const opId = parseInt(actualId);
    const mId = machineId ? parseInt(machineId) : null;
    let candidates;

    if (viewMode === 'hour' && slot.type === 'hour') {
      const key = `${opId}_${slot.date.toDateString()}_${slot.date.getHours()}`;
      candidates = taskLookup.byOperatorHour[key] || [];
    } else if (viewMode === 'day' && slot.type === 'day') {
      const key = `${opId}_${slot.date.toDateString()}`;
      candidates = taskLookup.byOperatorDay[key] || [];
    } else if (viewMode === 'week' && slot.type === 'week') {
      // Fallback for less-used views
      candidates = tasks.filter(task => {
        if (task.operator_id !== opId || !task.scheduled_start) return false;
        const taskStart = parseApiDate(task.scheduled_start);
        return taskStart >= slot.weekStart && taskStart < slot.weekEnd;
      });
    } else if (viewMode === 'month' && slot.type === 'month') {
      candidates = tasks.filter(task => {
        if (task.operator_id !== opId || !task.scheduled_start) return false;
        const taskStart = parseApiDate(task.scheduled_start);
        return taskStart.getMonth() === slot.date.getMonth() &&
               taskStart.getFullYear() === slot.date.getFullYear();
      });
    } else {
      return null;
    }

    // Filter by machine if this is a machine-operator combination
    if (mId) {
      candidates = candidates.filter(t => t.machine_id === mId);
    }

    return candidates[0] || null;
  };

  // Check if slot is occupied by NON-COMPLETED task (for drag/drop validation)
  const getScheduledTaskForSlot = (resourceId, slot) => {
    const task = getTaskForDisplay(resourceId, slot);
    // Return null if task is completed (slot is not "occupied" for scheduling purposes)
    return task && task.status !== 'COMPLETED' ? task : null;
  };

  // getPriorityColor and getStatusColor moved to module scope above

  const getScheduledTask = (resourceId, date) => {
    // Parse resource type and ID - handle both simple and composite IDs
    let resourceType, actualId;

    if (resourceId.startsWith('machine_') && resourceId.includes('_operator_')) {
      // Composite ID like "machine_1_operator_5"
      const parts = resourceId.split('_');
      resourceType = 'operator';
      actualId = parts[parts.length - 1]; // Get the operator ID from the end
    } else {
      // Simple ID like "machine_1" or "operator_5"
      [resourceType, actualId] = resourceId.split('_');
    }

    return tasks.find(task => {
      if (resourceType === 'operator') {
        // For operators, check the operator_id field
        return task.operator_id === parseInt(actualId) &&
               task.scheduled_date &&
               new Date(task.scheduled_date + 'T00:00:00').toDateString() === date.toDateString();
      }
      // For machines (shouldn't happen as we don't allow dropping on machines)
      return false;
    });
  };

  // getResourceStatusColor and getResourceIcon moved to module scope above

  if (loading) {
    return <div className="planner-loading">{t('plannerDashboard.loading')}</div>;
  }

  return (
    <div className="planner-dashboard">
      {/* Notification Display */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}
      {/* Header */}
      <div className="planner-header">
        <div className="header-left">
          <h1>📅 {t('plannerDashboard.title')}</h1>

          {/* View Mode Selector */}
          <>
              <div className="view-mode-selector">
                <button
                  className={`view-mode-btn ${viewMode === 'hour' ? 'active' : ''}`}
                  onClick={() => {
                    // Keep the current date when switching to hour view
                    setViewMode('hour');
                    showNotification(t('plannerDashboard.notifications.switchedToHour', { date: formatAppDate(viewStartDate) }), 'info');
                  }}
                >
                  {t('plannerDashboard.viewModes.hour')}
                </button>
                <button
                  className={`view-mode-btn ${viewMode === 'day' ? 'active' : ''}`}
                  onClick={() => {
                    // Keep the current date when switching to day view
                    setViewMode('day');
                    showNotification(t('plannerDashboard.notifications.switchedToDay', { date: formatAppDate(viewStartDate) }), 'info');
                  }}
                >
                  {t('plannerDashboard.viewModes.day')}
                </button>
              </div>

              {/* Navigation controls for hour view */}
              {viewMode === 'hour' && (
            <div className="date-navigation" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginLeft: '20px'
            }}>
              <button
                className="nav-btn"
                onClick={() => {
                  const prevDay = new Date(viewStartDate);
                  prevDay.setDate(prevDay.getDate() - 1);
                  setViewStartDate(prevDay);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                ← {t('plannerDashboard.previousDay')}
              </button>
              <div style={{
                padding: '4px 12px',
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '4px',
                fontWeight: 'bold'
              }}>
                {formatAppDate(viewStartDate, {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              <button
                className="nav-btn"
                onClick={() => {
                  const nextDay = new Date(viewStartDate);
                  nextDay.setDate(nextDay.getDate() + 1);
                  setViewStartDate(nextDay);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                {t('plannerDashboard.nextDay')} →
              </button>
              <button
                className="nav-btn"
                onClick={() => {
                  setViewStartDate(new Date());
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                  border: '1px solid #2196f3',
                  borderRadius: '4px',
                  color: '#2196f3',
                  cursor: 'pointer',
                  marginLeft: '10px'
                }}
              >
                {t('common:ui.today')}
              </button>
            </div>
              )}

              {/* Date Navigation */}
              <div className="date-navigation">
              <button onClick={() => {
                const newDate = new Date(viewStartDate);
                newDate.setDate(newDate.getDate() - 1);
                setViewStartDate(newDate);
              }}>◀</button>

              <span className="current-period">
                {viewMode === 'hour' && formatAppDate(viewStartDate, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                {viewMode === 'day' && `${formatAppDate(viewStartDate, { month: 'short', day: 'numeric' })} - ${(() => {
                  const endDate = new Date(viewStartDate);
                  endDate.setDate(viewStartDate.getDate() + 29);
                  return formatAppDate(endDate, { month: 'short', day: 'numeric', year: 'numeric' });
                })()}`}
              </span>

              <button onClick={() => {
                const newDate = new Date(viewStartDate);
                newDate.setDate(newDate.getDate() + 1);
                setViewStartDate(newDate);
              }}>▶</button>

              <button className="today-btn" onClick={() => setViewStartDate(new Date())}>
                {t('common:ui.today')}
              </button>
              </div>
            </>
          )}
        </div>
        <div className="header-actions">
          <div className="undo-redo-controls">
            <button
              className={`btn-undo ${historyIndex < 0 ? 'disabled' : ''}`}
              onClick={handleUndo}
              disabled={historyIndex < 0}
              title={t('plannerDashboard.undoTitle', { count: historyIndex + 1 })}
            >
              ↩️ {t('plannerDashboard.undo')}
            </button>
            <button
              className={`btn-redo ${historyIndex >= actionHistory.length - 1 ? 'disabled' : ''}`}
              onClick={handleRedo}
              disabled={historyIndex >= actionHistory.length - 1}
              title={t('plannerDashboard.redoTitle', { count: Math.max(0, actionHistory.length - historyIndex - 1) })}
            >
              ↪️ {t('plannerDashboard.redo')}
            </button>
          </div>
        </div>
      </div>

      {/* Planning View Content */}
      <div className="planner-content">
        {/* Sidebar with unscheduled tasks */}
        <div className="tasks-sidebar">
          <h3>📋 {t('plannerDashboard.unscheduledTasks')}</h3>
          <div className="task-filters">
            <select
              className="filter-select"
              value={selectedPO}
              onChange={handlePOChange}
            >
              <option value="">{t('plannerDashboard.allProductionOrders')}</option>
              {productionOrders.map(po => (
                <option key={po.production_order_id} value={`${po.production_order_id}|${po.po_number}`}>
                  {po.po_number} - {po.customer_name}
                </option>
              ))}
            </select>

            {selectedPO && orderLines.length > 0 && (
              <select
                className="filter-select"
                value={selectedOrderLine}
                onChange={(e) => setSelectedOrderLine(e.target.value)}
                style={{marginTop: '8px'}}
              >
                <option value="">{t('plannerDashboard.allOrderLines')}</option>
                {orderLines.map(line => (
                  <option key={line.production_order_line_id} value={line.production_order_line_id}>
                    {t('plannerDashboard.orderLineOption', { number: line.line_number, product: line.product_name, qty: line.order_quantity })}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div
            className="unscheduled-tasks"
            onDragOver={handleUnscheduledDragOver}
            onDrop={handleUnscheduleTask}
          >
            {tasks
              .filter(task =>
                (!task.scheduled_date || task.status === 'PENDING') &&
                (!selectedPO || task.isCurrentPO)
              )
              .map(task => (
                <div
                  key={task.task_id}
                  className="task-card"
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  style={{
                    borderLeft: `4px solid ${getPriorityColor(task.priority)}`
                  }}
                >
                  <div className="task-header">
                    <span className="task-code">{task.task_name || task.operation_name || t('plannerDashboard.task')}</span>
                    <span className={`priority-badge priority-${task.priority?.toLowerCase()}`}>
                      {task.priority}
                    </span>
                  </div>
                  <div className="task-title">{task.task_code}</div>
                  <div className="task-details">
                    <span className="task-po">📦 {task.po_number}</span>
                    <span className="task-duration">⏱ {task.estimated_duration_minutes} min</span>
                  </div>
                  <div className="task-product">{task.product_name}</div>
                </div>
              ))}
          </div>
        </div>

        {/* Main Timeline Grid */}
        <div className="timeline-container">
          <div className="timeline-grid planner-schedule" data-view={viewMode}>
            {/* Timeline Header */}
            <div className="timeline-header" style={{
              display: 'grid',
              gridTemplateColumns: `200px repeat(${timeSlots.length}, minmax(${viewMode === 'hour' ? '60px' : '80px'}, 1fr))`
            }}>
              <div className="corner-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <select
                  value={resourceFilter}
                  onChange={(e) => setResourceFilter(e.target.value)}
                  className="resource-filter-select"
                >
                  <option value="all">{t('plannerDashboard.resourceFilter.all')}</option>
                  <option value="machines">{t('plannerDashboard.resourceFilter.machines')}</option>
                  <option value="operators">{t('plannerDashboard.resourceFilter.operators')}</option>
                </select>
                <div className="expand-collapse-btns">
                  <button onClick={expandAllMachines} title={t('plannerDashboard.expandAll')}>&#8862;</button>
                  <button onClick={collapseAllMachines} title={t('plannerDashboard.collapseAll')}>&#8863;</button>
                </div>
              </div>
              {timeSlots.map((slot, index) => (
                <div key={index} className={`date-header ${slot.type}-header ${slot.isToday ? 'today' : ''} ${slot.isWeekend ? 'weekend' : ''} ${slot.isCurrentWeek ? 'current-week' : ''} ${slot.isCurrentMonth ? 'current-month' : ''}`}>
                  {slot.type === 'hour' && (
                    <>
                      <div className="hour-text">{slot.displayText}</div>
                      {slot.headerText && <div className="hour-date">{slot.headerText}</div>}
                    </>
                  )}
                  {slot.type === 'day' && (
                    <>
                      <div className="date-day">{slot.dayName}</div>
                      <div className="date-number">{slot.dayNumber}</div>
                      <div className="date-month">{slot.month}</div>
                    </>
                  )}
                  {slot.type === 'week' && (
                    <>
                      <div className="week-text">{slot.displayText}</div>
                      <div className="week-range">{slot.headerText}</div>
                    </>
                  )}
                  {slot.type === 'month' && (
                    <>
                      <div className="month-text">{slot.displayText}</div>
                      <div className="month-year">{slot.headerText}</div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Resource Rows (Machines and Operators) */}
            {visibleResources.map(resource => (
              <div key={resource.resource_id}
                className={`department-row ${resource.type === 'machine' ? 'machine-row' : resource.is_nested ? 'operator-row nested' : 'operator-row'}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${timeSlots.length}, minmax(${viewMode === 'hour' ? '60px' : '80px'}, 1fr))`
                }}>
                <div
                  className={`department-info machine-info ${selectedResource?.resource_id === resource.resource_id ? 'selected' : ''}`}
                  onClick={() => setSelectedResource(resource)}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    resourceTooltipRef.current?.show({
                      resource,
                      x: rect.right + 10,
                      y: rect.top
                    });
                  }}
                  onMouseLeave={() => resourceTooltipRef.current?.hide()}
                >
                  <div className="machine-status-indicator" style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getResourceStatusColor(resource),
                    position: 'absolute',
                    top: '12px',
                    right: '12px'
                  }}></div>
                  <div className="dept-name machine-name" style={{
                    paddingLeft: resource.is_nested ? '40px' : '0',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {resource.type === 'machine' && operatorCountByMachine[resource.machine_id] > 0 && (
                      <span
                        className={`machine-toggle ${expandedMachines.has(resource.machine_id) ? 'expanded' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleMachine(resource.machine_id); }}
                      >&#9654;</span>
                    )}
                    {resource.is_nested && (
                      <span style={{
                        position: 'absolute',
                        left: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'rgba(255, 255, 255, 0.3)',
                        fontSize: '10px'
                      }}>└─</span>
                    )}
                    {getResourceIcon(resource)} {resource.display_name}
                    {resource.type === 'machine' && operatorCountByMachine[resource.machine_id] > 0 && (
                      <span className="operator-count">({operatorCountByMachine[resource.machine_id]})</span>
                    )}
                  </div>
                  <div className="dept-code machine-code" style={{
                    paddingLeft: resource.is_nested ? '30px' : '0'
                  }}>
                    <span>{resource.type === 'machine' ? resource.machine_code : resource.username}</span>
                    <span style={{ marginLeft: '8px', fontSize: '10px', opacity: 0.7 }}>
                      {resource.type === 'machine' ? resource.machine_type : resource.role}
                    </span>
                  </div>
                </div>

                {/* Time slots for this resource */}
                {(() => {
                  let skipNext = 0;
                  return timeSlots.map((slot, slotIndex) => {
                    // Check if we should skip this slot (covered by previous spanning task)
                    if (skipNext > 0) {
                      skipNext--;
                      return null;
                    }

                    // Get task for display (including completed tasks)
                    const taskForDisplay = getTaskForDisplay(resource.resource_id, slot);
                    // Check if slot is occupied by non-completed task (for drag/drop validation)
                    const scheduledTask = getScheduledTaskForSlot(resource.resource_id, slot);
                    const taskSpanInfo = taskForDisplay ? getTaskSpanInfo(taskForDisplay, slot, slotIndex, timeSlots) : null;
                    const isUnavailable = resource.type === 'machine' && resource.status === 'MAINTENANCE';
                    const isOccupied = scheduledTask !== null;
                    const isAvailable = isSlotAvailable(resource.resource_id, slot);

                    // Get all tasks for this day slot if in day view
                    let dayTasks = [];
                    let hourlyDistribution = {};
                    let hasConflictsInDay = false;

                    if (viewMode === 'day' && resource.type === 'operator') {
                      // Parse operator ID and machine ID for both simple and machine_operator formats
                      let operatorId;
                      let machineIdForFilter = null;
                      if (resource.resource_id.startsWith('machine_') && resource.resource_id.includes('_operator_')) {
                        const parts = resource.resource_id.split('_');
                        operatorId = parts[parts.length - 1]; // Get last part for machine_X_operator_Y format
                        machineIdForFilter = parseInt(parts[1]); // Get machine ID for filtering
                      } else {
                        [, operatorId] = resource.resource_id.split('_'); // Simple operator_X format
                      }

                      // Get tasks for this operator, optionally filtered by machine
                      dayTasks = getAllTasksForDaySlot(operatorId, slot).filter(task => {
                        // If this is a machine-operator combo, only show tasks for this machine
                        if (machineIdForFilter) {
                          return task.machine_id === machineIdForFilter;
                        }
                        // For standalone operators, show ALL their tasks regardless of machine assignment
                        return true;
                      });

                      // Calculate hourly distribution and check for conflicts
                      dayTasks.forEach(task => {
                        if (task.scheduled_start) {
                          const hour = parseApiDate(task.scheduled_start).getHours();
                          hourlyDistribution[hour] = (hourlyDistribution[hour] || 0) + 1;
                        }
                      });

                      // Check if any tasks in this day have machine conflicts (uses lookup map)
                      if (viewMode === 'day' && dayTasks.length > 0) {
                        for (const operatorTask of dayTasks) {
                          if (operatorTask.machine_id && operatorTask.scheduled_start &&
                              operatorTask.status !== 'COMPLETED' && operatorTask.status !== 'CANCELLED') {

                            const taskStart = parseApiDate(operatorTask.scheduled_start);
                            const taskEnd = operatorTask.scheduled_end ? parseApiDate(operatorTask.scheduled_end) :
                                          new Date(taskStart.getTime() + (operatorTask.estimated_duration_minutes || 60) * 60000);
                            const dateStr = taskStart.toDateString();
                            const startHour = taskStart.getHours();
                            const endHour = Math.min(
                              dateStr === taskEnd.toDateString() ? taskEnd.getHours() + (taskEnd.getMinutes() > 0 ? 1 : 0) : 24,
                              24
                            );

                            for (let h = startHour; h < endHour; h++) {
                              const mKey = `${operatorTask.machine_id}_${dateStr}_${h}`;
                              const machineTasksInHour = taskLookup.byMachineHour[mKey] || [];
                              if (machineTasksInHour.some(t =>
                                t.task_id !== operatorTask.task_id &&
                                t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
                              )) {
                                hasConflictsInDay = true;
                                break;
                              }
                            }
                            if (hasConflictsInDay) break;
                          }
                        }
                      }
                    }

                    // If this is the start of a spanning task, skip the next slots it covers
                    if (taskSpanInfo?.isStart && taskSpanInfo.spanCount > 1) {
                      skipNext = taskSpanInfo.spanCount - 1;
                    }

                    // Don't render if this slot is covered by a task that started earlier
                    if (taskSpanInfo?.continuesThrough) {
                      return null;
                    }

                    // Determine the grid column span for multi-slot tasks
                    let gridColumnStyle = {};
                    if (taskSpanInfo?.isStart && taskSpanInfo.spanCount > 1) {
                      gridColumnStyle = {
                        gridColumn: `span ${taskSpanInfo.spanCount}`,
                        position: 'relative',
                        zIndex: 2
                      };
                    }

                    return (
                      <div
                        key={slotIndex}
                        className={`time-slot ${slot.isWeekend ? 'weekend' : ''} ${isUnavailable ? 'maintenance' : ''} ${isOccupied ? 'occupied' : ''} ${!isAvailable && !isOccupied ? 'unavailable' : ''} ${viewMode === 'day' ? 'day-view-slot' : ''} ${
                          viewMode === 'day' && dayTasks.length > 0 ? 'has-day-tasks' : ''
                        }`}
                        onDragOver={(e) => handleDragOver(e, resource.resource_id, slot)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, resource.resource_id, slot)}
                        style={{
                          opacity: isUnavailable ? 0.5 : 1,
                          cursor: resource.type === 'machine' ? 'not-allowed' : isOccupied ? 'not-allowed' : 'default',
                          ...gridColumnStyle,
                          position: 'relative',
                          // Apply dark grey background for ALL day view slots
                          ...(viewMode === 'day' ? {
                            background: 'rgba(60, 60, 60, 0.2)' // Lighter dark grey for all slots
                          } : {})
                        }}
                        title={
                          resource.type === 'machine'
                            ? t('plannerDashboard.slotTitles.cannotAssignMachines')
                            : isOccupied
                            ? t('plannerDashboard.slotTitles.occupied')
                            : isUnavailable
                            ? t('plannerDashboard.slotTitles.maintenance')
                            : ''
                        }
                      >
                        {/* Show stacked tasks in day view for operators with tasks */}
                        {viewMode === 'day' && dayTasks.length > 0 ? (
                          <div
                            className="task-stack"
                            onClick={() => {
                              // Switch to hour view for this specific day
                              setViewStartDate(new Date(slot.date)); // Set the focused date
                              setViewMode('hour');
                              showNotification(t('plannerDashboard.notifications.switchedToHour', { date: formatAppDate(slot.date) }), 'info');
                            }}
                            style={{
                              position: 'relative',
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer',
                              backgroundColor: hasConflictsInDay ?
                                'rgba(255, 23, 68, 0.3)' :  // Light red for conflicts
                                'rgba(76, 175, 80, 0.2)',    // Light green for normal days with tasks
                              borderRadius: '4px',
                              padding: '4px',
                              boxSizing: 'border-box',
                              border: hasConflictsInDay ? '2px solid #ff1744' : 'none'
                            }}
                          >
                            {/* Conflict indicator */}
                            {hasConflictsInDay && (
                              <div style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                backgroundColor: '#ff1744',
                                color: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                zIndex: 1000,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                animation: 'pulse-conflict 2s infinite'
                              }}>
                                ❗
                              </div>
                            )}
                            {/* Show single task card or count indicator */}
                            {dayTasks.length === 1 ? (
                              <div
                                className="stacked-task"
                                draggable={!dayTasks[0].isOtherPO}
                                onDragStart={(e) => handleDragStart(e, dayTasks[0])}
                                style={{
                                  position: 'relative',
                                  backgroundColor: dayTasks[0].isOtherPO ? '#4a4a4a' : getStatusColor(dayTasks[0].status),
                                  borderLeft: `3px solid ${dayTasks[0].isOtherPO ? '#666' : getPriorityColor(dayTasks[0].priority)}`,
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                  fontSize: '11px'
                                }}
                              >
                                <div style={{
                                  fontWeight: 'bold',
                                  fontSize: '10px',
                                  color: '#fff',
                                  wordWrap: 'break-word',
                                  overflowWrap: 'break-word',
                                  hyphens: 'auto',
                                  maxWidth: '100%',
                                  lineHeight: '1.2',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {dayTasks[0].task_name || dayTasks[0].operation_name || t('plannerDashboard.task')}
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  position: 'relative',
                                  backgroundColor: '#4a5568',
                                  borderLeft: '3px solid #718096',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                  fontSize: '11px',
                                  textAlign: 'center'
                                }}
                              >
                                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>
                                  {dayTasks.length}
                                </div>
                                <div style={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.7)' }}>
                                  {t('plannerDashboard.tasksLower', { count: dayTasks.length })}
                                </div>
                              </div>
                            )}
                            {/* Hour distribution indicator */}
                            <div style={{
                              position: 'absolute',
                              top: '2px',
                              left: '2px',
                              right: '2px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              display: 'flex',
                              gap: '1px',
                              zIndex: 12
                            }}>
                              {[8, 9, 10, 11, 12, 13, 14, 15, 16].map(hour => (
                                <div
                                  key={hour}
                                  style={{
                                    flex: 1,
                                    backgroundColor: hourlyDistribution[hour] ?
                                      hourlyDistribution[hour] > 1 ? '#ff5722' : '#4caf50'
                                      : 'transparent',
                                    opacity: hourlyDistribution[hour] ? 1 : 0.3,
                                    borderRadius: '1px'
                                  }}
                                  title={t('plannerDashboard.hourDistributionTitle', { hour, count: hourlyDistribution[hour] || 0 })}
                                />
                              ))}
                            </div>
                          </div>
                        ) : viewMode !== 'day' && taskForDisplay && taskSpanInfo?.shouldDisplay && (() => {
                          // Parse resource ID to extract machine ID for conflict checking
                          let extractedMachineId = null;
                          if (resource.resource_id.startsWith('machine_') && resource.resource_id.includes('_operator_')) {
                            const parts = resource.resource_id.split('_');
                            extractedMachineId = parseInt(parts[1]); // Get machine ID from machine_X_operator_Y
                          }

                          // Check for machine conflicts
                          const conflicts = extractedMachineId ? checkMachineConflicts(extractedMachineId, slot) : [];
                          const hasConflict = conflicts.length > 0;

                          // Check if conflicts are from different POs
                          const crossPOConflict = hasConflict && taskForDisplay.po_number &&
                            conflicts.some(t => t.po_number && t.po_number !== taskForDisplay.po_number);

                          const conflictingOperators = hasConflict ?
                            [...new Set(conflicts.map(c => c.operator_name || t('plannerDashboard.operatorFallback', { id: c.operator_id })))].join(', ') : '';
                          const conflictingPOs = crossPOConflict ?
                            [...new Set(conflicts.filter(t => t.po_number !== taskForDisplay.po_number).map(t => t.po_number))].join(', ') : '';

                          return (
                            <div
                              className={`scheduled-task ${taskSpanInfo.spanCount > 1 ? 'multi-slot' : ''} ${hasConflict ? 'has-conflict' : ''} ${crossPOConflict ? 'cross-po-conflict' : ''} ${taskForDisplay.status === 'COMPLETED' ? 'task-completed' : ''} ${taskForDisplay.isOtherPO ? 'other-po-task' : ''}`}
                              draggable={taskForDisplay.status !== 'COMPLETED' && !taskForDisplay.isOtherPO} // Don't allow dragging completed tasks or other PO tasks
                              onDragStart={(e) => handleDragStart(e, taskForDisplay)}
                              style={{
                                backgroundColor: taskForDisplay.isOtherPO ? '#4a4a4a' : hasConflict ? '#ff5722' : (taskForDisplay.status === 'COMPLETED' ? '#27ae60' : getStatusColor(taskForDisplay.status)),
                                borderLeft: `3px solid ${taskForDisplay.isOtherPO ? '#666' : hasConflict ? '#ff1744' : (taskForDisplay.status === 'COMPLETED' ? '#1e7e34' : getPriorityColor(taskForDisplay.priority))}`,
                                '--task-color': taskForDisplay.isOtherPO ? '#4a4a4a' : hasConflict ? '#ff5722' : getStatusColor(taskForDisplay.status),
                                boxShadow: taskForDisplay.status === 'COMPLETED' ? '0 2px 4px rgba(39, 174, 96, 0.3)' : (hasConflict ? '0 0 10px rgba(255, 23, 68, 0.5)' : 'none'),
                                animation: hasConflict && !taskForDisplay.isOtherPO ? 'pulse-conflict 2s infinite' : 'none',
                                opacity: taskForDisplay.isOtherPO ? 0.5 : taskForDisplay.status === 'COMPLETED' ? 0.9 : 1
                              }}
                              title={taskForDisplay.isOtherPO ?
                                `${t('plannerDashboard.conflict.otherOrder', { po: taskForDisplay.po_number || t('plannerDashboard.conflict.unknownPo') })}\n${taskForDisplay.task_name} - ${taskForDisplay.task_code}` :
                                hasConflict ?
                                `⚠️ ${t('plannerDashboard.conflict.machineConflict', { count: conflicts.length, machine: selectedResource?.display_name || t('plannerDashboard.conflict.thisMachine') })}\n${crossPOConflict ? `${t('plannerDashboard.conflict.crossPo', { pos: conflictingPOs })}\n` : ''}${t('plannerDashboard.conflict.operators', { operators: conflictingOperators })}` :
                                `${taskForDisplay.task_name} - ${taskForDisplay.task_code}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(e, taskForDisplay);
                              }}
                              onContextMenu={(e) => handleTaskContextMenu(e, taskForDisplay)}
                              onMouseEnter={(e) => handleTaskMouseEnter(e, taskForDisplay)}
                              onMouseLeave={handleTaskMouseLeave}
                            >
                              {hasConflict && (
                                <div style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  backgroundColor: '#ff1744',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  zIndex: 1000,
                                  animation: 'pulse-conflict 2s infinite'
                                }}>
                                  ⚠️
                                </div>
                              )}
                              <div className="scheduled-task-code">
                                {taskForDisplay.isOtherPO && <span style={{ fontSize: '9px', opacity: 0.7 }}>[{taskForDisplay.po_number}] </span>}
                                {taskForDisplay.task_name || taskForDisplay.operation_name || t('plannerDashboard.task')}
                              </div>
                              <div className="scheduled-task-name">
                                {hasConflict && <span style={{ color: '#ffcdd2', fontSize: '10px' }}>⚠️ </span>}
                                {taskForDisplay.task_code}
                              </div>
                              <div className="scheduled-task-time">
                                {taskForDisplay.estimated_duration_minutes >= 60
                                  ? `${Math.floor(taskForDisplay.estimated_duration_minutes / 60)}h ${taskForDisplay.estimated_duration_minutes % 60}m`
                                  : `${taskForDisplay.estimated_duration_minutes}m`}
                              </div>
                              {hasConflict && (
                                <div style={{
                                  fontSize: '10px',
                                  color: '#ffcdd2',
                                  marginTop: '2px'
                                }}>
                                  {t('plannerDashboard.conflict.overlapping', { count: conflicts.length })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  });
                })()}
              </div>
            ))}
          </div>
        </div>
        </div>

        {/* Bottom Status Bar */}
        <div className="planner-footer">
          <div className="footer-stats">
            <span>📋 {t('plannerDashboard.footer.total')}: {tasks.length}</span>
            <span>✅ {t('plannerDashboard.footer.scheduled')}: {tasks.filter(task => task.scheduled_date).length}</span>
            <span>⏳ {t('common:ui.pending')}: {tasks.filter(task => task.status === 'PENDING').length}</span>
            <span>🚀 {t('common:ui.inProgress')}: {tasks.filter(task => task.status === 'IN_PROGRESS').length}</span>
          </div>
          {/* Footer actions removed */}
        </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10001
          }}
        >
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('edit')}
          >
            ✏️ {t('plannerDashboard.contextMenu.editDetails')}
          </div>
          <div className="context-menu-divider"></div>
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('complete')}
          >
            ✅ {t('plannerDashboard.contextMenu.markComplete')}
          </div>
          <div className="context-menu-divider"></div>
          <div className="context-menu-submenu">
            <div className="context-menu-item-header">⚡ {t('plannerDashboard.contextMenu.setPriority')}</div>
            <div
              className="context-menu-item context-menu-subitem"
              onClick={() => handleContextMenuAction('urgent-priority')}
            >
              🔴 {t('plannerDashboard.priorities.URGENT')}
            </div>
            <div
              className="context-menu-item context-menu-subitem"
              onClick={() => handleContextMenuAction('high-priority')}
            >
              🟠 {t('plannerDashboard.priorities.HIGH')}
            </div>
            <div
              className="context-menu-item context-menu-subitem"
              onClick={() => handleContextMenuAction('normal-priority')}
            >
              🟢 {t('plannerDashboard.priorities.NORMAL')}
            </div>
          </div>
          <div className="context-menu-divider"></div>
          <div
            className="context-menu-item"
            onClick={() => handleContextMenuAction('duplicate')}
          >
            📋 {t('plannerDashboard.contextMenu.duplicate')}
          </div>
          <div className="context-menu-divider"></div>
          <div
            className="context-menu-item context-menu-danger"
            onClick={() => handleContextMenuAction('delete')}
          >
            🗑️ {t('plannerDashboard.deleteTask')}
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <div className="task-modal-overlay" onClick={handleCloseTaskModal}>
          <div className="task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-modal-header">
              <h2>{t('plannerDashboard.modal.title')}</h2>
              <button className="modal-close" onClick={handleCloseTaskModal}>×</button>
            </div>
            <div className="task-modal-content">
              <div className="task-modal-form">
                <div className="form-group">
                  <label>{t('plannerDashboard.modal.taskCode')}</label>
                  <input
                    type="text"
                    value={selectedTask.task_code}
                    readOnly
                    className="form-control readonly"
                  />
                </div>

                <div className="form-group">
                  <label>{t('plannerDashboard.modal.taskName')}</label>
                  <input
                    type="text"
                    id="task-name"
                    defaultValue={selectedTask.task_name || selectedTask.operation_name}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>{t('common:ui.description')}</label>
                  <textarea
                    id="task-description"
                    defaultValue={selectedTask.description || ''}
                    className="form-control"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('common:ui.priority')}</label>
                    <select
                      id="task-priority"
                      defaultValue={selectedTask.priority}
                      className="form-control"
                    >
                      <option value="LOW">{t('plannerDashboard.priorities.LOW')}</option>
                      <option value="NORMAL">{t('plannerDashboard.priorities.NORMAL')}</option>
                      <option value="HIGH">{t('plannerDashboard.priorities.HIGH')}</option>
                      <option value="URGENT">{t('plannerDashboard.priorities.URGENT')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('common:ui.status')}</label>
                    <select
                      id="task-status"
                      defaultValue={selectedTask.status}
                      className="form-control"
                    >
                      <option value="PENDING">{t('plannerDashboard.statuses.PENDING')}</option>
                      <option value="ASSIGNED">{t('plannerDashboard.statuses.ASSIGNED')}</option>
                      <option value="IN_PROGRESS">{t('plannerDashboard.statuses.IN_PROGRESS')}</option>
                      <option value="PAUSED">{t('plannerDashboard.statuses.PAUSED')}</option>
                      <option value="COMPLETED">{t('plannerDashboard.statuses.COMPLETED')}</option>
                      <option value="CANCELLED">{t('plannerDashboard.statuses.CANCELLED')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('plannerDashboard.modal.estimatedDuration')}</label>
                    <input
                      type="number"
                      id="task-duration"
                      defaultValue={selectedTask.estimated_duration_minutes || 60}
                      min="15"
                      step="15"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('plannerDashboard.modal.scheduledStart')}</label>
                    <input
                      type="datetime-local"
                      id="task-start"
                      defaultValue={selectedTask.scheduled_start ?
                        toLocalDatetimeInput(selectedTask.scheduled_start) : ''}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('plannerDashboard.modal.instructions')}</label>
                  <textarea
                    id="task-instructions"
                    defaultValue={selectedTask.instructions || ''}
                    className="form-control"
                    rows="3"
                    placeholder={t('plannerDashboard.modal.instructionsPlaceholder')}
                  />
                </div>

                <div className="form-info">
                  <div>
                    <strong>{t('plannerDashboard.modal.assignedTo')}:</strong> {selectedTask.operator_name || t('plannerDashboard.unassigned')}
                  </div>
                  <div>
                    <strong>{t('plannerDashboard.tooltip.machine')}:</strong> {selectedTask.machine_name || t('plannerDashboard.modal.noMachine')}
                  </div>
                  <div>
                    <strong>{t('plannerDashboard.modal.created')}:</strong> {formatAppDate(selectedTask.created_at, 'datetime')}
                  </div>
                </div>
              </div>
            </div>
            <div className="task-modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => handleTaskDelete(selectedTask.task_id)}
              >
                🗑️ {t('plannerDashboard.deleteTask')}
              </button>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseTaskModal}
                >
                  {t('common:ui.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    // Gather updated values from form
                    const updatedData = {
                      task_name: document.getElementById('task-name').value,
                      description: document.getElementById('task-description').value,
                      priority: document.getElementById('task-priority').value,
                      status: document.getElementById('task-status').value,
                      estimated_duration_minutes: parseInt(document.getElementById('task-duration').value),
                      scheduled_start: document.getElementById('task-start').value ?
                        new Date(document.getElementById('task-start').value).toISOString() : null,
                      instructions: document.getElementById('task-instructions').value
                    };

                    // Calculate scheduled_end based on duration
                    if (updatedData.scheduled_start) {
                      const endTime = new Date(updatedData.scheduled_start);
                      endTime.setMinutes(endTime.getMinutes() + updatedData.estimated_duration_minutes);
                      updatedData.scheduled_end = endTime.toISOString();
                    }

                    handleTaskUpdate(updatedData);
                  }}
                >
                  💾 {t('plannerDashboard.modal.saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip components - isolated state, no parent re-renders on hover */}
      <TaskTooltipDisplay ref={taskTooltipRef} />
      <ResourceTooltipDisplay ref={resourceTooltipRef} />
    </div>
  );
}

export default PlannerDashboard;